import type { SkinTone } from 'frimousse'

/**
 * "Frequent" reactions for this browser (design 35b). Counts live in
 * localStorage — a per-viewer convenience, so every read and write is
 * guarded and the defaults stand in when storage is unavailable.
 */
const STORAGE_KEY = 'moly.reactions.frequent'
const SKIN_TONE_KEY = 'moly.reactions.skinTone'

export const DEFAULT_FREQUENT = ['👍', '🎉', '👀', '✅', '🙏', '😄', '🔥', '❤️'] as const

function readCounts(): Record<string, number> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, number>) : {}
  } catch {
    return {}
  }
}

export function recordEmojiUse(emoji: string) {
  try {
    const counts = readCounts()
    counts[emoji] = (counts[emoji] ?? 0) + 1
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(counts))
  } catch {
    // Storage blocked: frequent stays at the defaults.
  }
}

/** Most-used first, topped up with the defaults so the row is always full. */
export function getFrequentEmoji(limit = 8): Array<string> {
  const counts = readCounts()
  const used = Object.keys(counts).sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0))
  const out: Array<string> = []
  for (const emoji of [...used, ...DEFAULT_FREQUENT]) {
    if (!out.includes(emoji)) out.push(emoji)
    if (out.length === limit) break
  }
  return out
}

export function readSkinTone(): SkinTone {
  try {
    const value = window.localStorage.getItem(SKIN_TONE_KEY)
    return (value as SkinTone | null) ?? 'none'
  } catch {
    return 'none'
  }
}

export function writeSkinTone(value: SkinTone) {
  try {
    window.localStorage.setItem(SKIN_TONE_KEY, value)
  } catch {
    // ignore
  }
}
